import { FC, Ref } from 'react'
import cx from 'classnames'
import { useTranslation } from 'next-i18next'

import { Input } from 'ui'

import { numberToCurrency } from 'utils'

type PriceComboboxProps = {
  prices: number[]
  transformPrice?: (price: string) => string
  listboxId: string
  listboxExpanded: boolean
  textboxRef?: Ref<HTMLInputElement>
  textboxName: string
  textboxLabel: string
  textboxValue: number | null
  textboxPlaceholder: string
  textboxOnChangeMethod: (value: number | null) => void
  textboxOnFocus: () => void
  onTextboxSelection?: () => void
}

const getValueWithDigitsOnly = (value: string): number =>
  Number(value.replace(/\D/g, ''))

const formatInputValue = (value: number | null): string =>
  value ? new Intl.NumberFormat('en-US').format(value) : ''

const PriceCombobox: FC<PriceComboboxProps> = ({
  prices,
  transformPrice = (price: string) => price,
  listboxId,
  listboxExpanded,
  textboxRef,
  textboxName,
  textboxLabel,
  textboxValue,
  textboxPlaceholder,
  textboxOnChangeMethod,
  textboxOnFocus,
  onTextboxSelection,
}) => {
  const { t } = useTranslation('common')
  return (
    <div
      role="combobox"
      aria-haspopup="listbox"
      aria-owns={listboxId}
      aria-controls={listboxId}
      aria-expanded={listboxExpanded}
      aria-labelledby={textboxName}
    >
      <Input
        ref={textboxRef}
        role="textbox"
        name={textboxName}
        id={textboxName}
        label={textboxLabel}
        value={formatInputValue(textboxValue)}
        placeholder={textboxPlaceholder}
        hideLabel
        onChange={(e: React.FormEvent<HTMLInputElement>) =>
          textboxOnChangeMethod(getValueWithDigitsOnly(e.currentTarget.value))
        }
        onFocus={textboxOnFocus}
      />
      <ul
        id={listboxId}
        role="listbox"
        className={cx('mt-8 space-y-8 body-sm', {
          'invisible opacity-0': !listboxExpanded,
        })}
      >
        <li role="option" aria-selected={!textboxValue}>
          <button
            className="hover:text-teal-3"
            onClick={() => {
              textboxOnChangeMethod(null)
              onTextboxSelection ? onTextboxSelection() : null
            }}
          >
            {t('ui.any-price')}
          </button>
        </li>
        {prices.map((value: number) => (
          <li key={value} role="option" aria-selected={value === textboxValue}>
            <button
              className={cx('hover:text-teal-3', {
                'font-bold text-teal-3': textboxValue === value,
              })}
              onClick={() => {
                textboxOnChangeMethod(value)
                onTextboxSelection ? onTextboxSelection() : null
              }}
            >
              {transformPrice(numberToCurrency(value))}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PriceCombobox
